import { useMemo } from 'react';
import { CurrentUserInterface } from '../model/types';
import { useUser } from '../api/useUser';
import { CurrentUser } from '../model/CurrentUser';

type OnboardingState = 'NEEDS_ACCOUNT' | 'NEEDS_SHOP' | 'COMPLETED';

export type AuthContextType = {
  sessionUser: CurrentUserInterface | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  /** @deprecated Use `sessionUser` instead */
  currentShopId: string | null;
  needsShopCreation: boolean;

  onboardingState: OnboardingState;
};

const emptyShops: CurrentUser['shops'] = [];

/**
 * Custom hook to manage authentication state and user data.
 */
export const useAuthContext = (): AuthContextType => {
  const { user: sessionUser, isLoading } = useUser();

  const currentShop = sessionUser?.shop ?? null;
  const currentShopId = sessionUser?.currentShopId || null;
  const availableShops = sessionUser?.shops ?? emptyShops;

  const needsShopCreation = !isLoading && !currentShop && availableShops.length === 0;

  const onboardingState: OnboardingState = useMemo(() => {
    switch (true) {
      case !sessionUser:
        return 'NEEDS_ACCOUNT';
      case !sessionUser?.currentShopId:
        return 'NEEDS_SHOP';
      default:
        return 'COMPLETED';
    }
  }, [sessionUser]);

  return useMemo(
    () => ({
      sessionUser,
      isLoading,
      // The session is authoritative. Access token bootstrap happens
      // transparently inside apiFetcher (401 → refresh-via-cookie → retry).
      isAuthenticated: !!sessionUser,

      currentShopId,
      onboardingState,
      needsShopCreation,
    }),
    [currentShopId, isLoading, needsShopCreation, onboardingState, sessionUser]
  );
};
